import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Globe,
  GripVertical,
  ImagePlus,
  Loader2,
  Map as MapIcon,
  Menu,
  Palette,
  Pencil,
  Plus,
  QrCode,
  Save,
  Store,
  Trash2,
  Upload,
} from 'lucide-react';
import QRCode from 'qrcode';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import ZonePolygonMap from '@/components/storefront/ZonePolygonMap';
import ImageUrlField from '@/components/storefront/ImageUrlField';
import RichTextEditor from '@/components/storefront/RichTextEditor';
import StoreCardPaymentTab from '@/components/settings/StoreCardPaymentTab';
import { useUIStore } from '@/store/uiStore';
import { saasApi, storefrontAdminApi } from '@/services/api';
import { resolveFileUrl } from '@/utils/fileUrl';

type Tab = 'general' | 'theme' | 'catalogue' | 'zones' | 'pages' | 'domain' | 'card_payment' | 'customers';
const TAB_IDS: Tab[] = ['general', 'theme', 'catalogue', 'zones', 'pages', 'domain', 'card_payment', 'customers'];
const LEGACY_TAB_MAP: Record<string, Tab> = { stripe: 'card_payment', teya: 'card_payment' };

type PageDraft = {
  name?: string;
  title: string;
  slug: string;
  page_type: string;
  is_published: number;
  show_in_footer: number;
  show_in_header: number;
  show_contact_form: number;
  content: string;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  og_image: string;
  hero_enabled: number;
  hero_image: string;
  hero_title: string;
  hero_subtitle: string;
  hero_cta_label: string;
  hero_cta_url: string;
  sort_order: number;
};

const emptyPage = (): PageDraft => ({
  title: '',
  slug: '',
  page_type: 'custom',
  is_published: 1,
  show_in_footer: 1,
  show_in_header: 0,
  show_contact_form: 0,
  content: '',
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  og_image: '',
  hero_enabled: 0,
  hero_image: '',
  hero_title: '',
  hero_subtitle: '',
  hero_cta_label: '',
  hero_cta_url: '',
  sort_order: 10,
});

export default function PublicStorePage() {
  const { toggleSidebar } = useUIStore();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const raw = searchParams.get('tab') || '';
    const mapped = (LEGACY_TAB_MAP[raw] || raw) as Tab;
    return mapped && TAB_IDS.includes(mapped) ? mapped : 'general';
  });
  const [form, setForm] = useState<Record<string, any>>({});
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const selectTab = (id: Tab) => {
    setTab(id);
    setSearchParams(id === 'general' ? {} : { tab: id }, { replace: true });
  };

  useEffect(() => {
    const raw = searchParams.get('tab') || '';
    if (!raw) return;
    const mapped = (LEGACY_TAB_MAP[raw] || raw) as Tab;
    if (LEGACY_TAB_MAP[raw]) {
      setSearchParams({ tab: mapped }, { replace: true });
      return;
    }
    if (TAB_IDS.includes(mapped) && mapped !== tab) setTab(mapped);
  }, [searchParams, tab, setSearchParams]);

  const { data, isLoading } = useQuery({
    queryKey: ['storefrontAdminSettings'],
    queryFn: async () => (await storefrontAdminApi.getSettings()).data.message,
  });

  const catalogue = useQuery({
    queryKey: ['storefrontCatalogue', search, categoryFilter],
    queryFn: async () =>
      (
        await storefrontAdminApi.listCatalogue({
          search: search || undefined,
          category: categoryFilter || undefined,
          published_categories_only: categoryFilter ? 0 : 1,
          limit: 120,
        })
      ).data.message,
  });

  const zones = useQuery({
    queryKey: ['storefrontZones'],
    queryFn: async () => (await storefrontAdminApi.listZones()).data.message as any[],
  });

  const setupWarnings = (() => {
    const warnings: string[] = [];
    if (!form.enable_storefront) warnings.push('Public store is disabled — enable it under General.');
    const pubCats = (catalogue.data?.categories || []).filter((c: any) => c.show_on_storefront);
    if (catalogue.data && pubCats.length === 0) {
      warnings.push('No categories published — publish at least one category under Catalogue.');
    }
    if (form.allow_delivery && Array.isArray(zones.data) && zones.data.length === 0) {
      warnings.push('Delivery is on but no delivery zones are set — add zones under Delivery zones.');
    }
    return warnings;
  })();

  useEffect(() => {
    if (!data) return;
    setForm({
      ...data,
      primary_colour: data.primary_colour || data.accent_colour || '#0F766E',
      secondary_colour: data.secondary_colour || '#134E4A',
      link_colour: data.link_colour || data.primary_colour || data.accent_colour || '#0F766E',
      show_hero_banner: data.show_hero_banner ?? 1,
      show_prices_without_login: data.show_prices_without_login ?? 1,
      show_categories_carousel: data.show_categories_carousel ?? 1,
      show_featured_products: data.show_featured_products ?? 1,
      show_category_strips: data.show_category_strips ?? 1,
      show_categories_in_footer: data.show_categories_in_footer ?? 1,
      featured_section_title: data.featured_section_title || 'Popular products',
      browse_by_category: data.browse_by_category ?? 1,
      browse_by_brand: data.browse_by_brand ?? 0,
      browse_by_model: data.browse_by_model ?? 0,
      show_brands_carousel: data.show_brands_carousel ?? 1,
      show_models_carousel: data.show_models_carousel ?? 1,
      hero_banners: data.hero_banners || [],
      store_logo: data.store_logo || data.company_logo || '',
      logo_width: Number(data.logo_width) || 48,
      show_shop_name_with_logo: data.show_shop_name_with_logo ?? 1,
      show_logo_in_footer: data.show_logo_in_footer ?? 1,
      theme: data.theme === 'fresh' ? 'modern' : data.theme || 'modern',
      button_bg_colour: data.button_bg_colour || data.primary_colour || data.accent_colour || '#0F766E',
      button_fg_colour: data.button_fg_colour || '#FFFFFF',
      button_hover_bg_colour: data.button_hover_bg_colour || data.secondary_colour || '#134E4A',
      button_hover_fg_colour: data.button_hover_fg_colour || '#FFFFFF',
      show_announcement_bar: data.show_announcement_bar ?? 1,
      announcement_text:
        data.announcement_text ??
        'Order online for delivery or collection · UK delivery zones available',
      header_subtitle: data.header_subtitle ?? 'Online shop',
      search_placeholder: data.search_placeholder || 'Search products…',
      basket_label: data.basket_label || 'Basket',
      login_label: data.login_label || 'Log in',
      footer_tagline:
        data.footer_tagline ??
        'Order online for delivery or collection. Same catalogue as our shop floor.',
    });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => {
      const theme = payload.theme === 'fresh' ? 'modern' : payload.theme || 'modern';
      // Card payment tab owns these — never let Shop & payments / theme saves wipe them
      const {
        pay_online_stripe: _s,
        pay_online_teya: _t,
        pay_online_dojo: _d,
        ...rest
      } = payload;
      return storefrontAdminApi.saveSettings({
        ...rest,
        theme,
        logo_width: Math.min(160, Math.max(24, Number(payload.logo_width) || 48)),
      });
    },
    onSuccess: (res) => {
      toast.success('Public Store settings saved');
      const msg = res.data.message || {};
      setForm({
        ...msg,
        logo_width: Number(msg.logo_width) || 48,
        theme: msg.theme === 'fresh' ? 'modern' : msg.theme || 'modern',
        store_logo: msg.store_logo || msg.company_logo || '',
      });
      void qc.invalidateQueries({ queryKey: ['storefrontAdminSettings'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const tabs: { id: Tab; label: string }[] = [
    { id: 'general', label: 'Shop & payments' },
    { id: 'customers', label: 'Store customers' },
    { id: 'card_payment', label: 'Card payment' },
    { id: 'theme', label: 'Theme & design' },
    { id: 'catalogue', label: 'Catalogue & pricing' },
    { id: 'zones', label: 'Delivery zones' },
    { id: 'pages', label: 'Pages & SEO' },
    { id: 'domain', label: 'Domain & share' },
  ];

  const activeCardGateway = form.pay_online_teya
    ? 'Teya'
    : form.pay_online_stripe
      ? 'Stripe'
      : form.pay_online_dojo
        ? 'Dojo'
        : null;

  // Storefront SPA lives at /store on the active host (custom domain or tenant subdomain)
  const publicUrl = normalizeStorefrontUrl(form.public_url || form.active_store_host || '');
  const shopDisplayName = form.shop_display_name || 'Online shop';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <TrialBanner />
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
          <button type="button" onClick={toggleSidebar} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Store className="w-5 h-5 text-teal-700" />
              Public Store
            </h1>
            <p className="text-sm text-gray-500">Theme, catalogue, delivery, payments — your online shop</p>
          </div>
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-secondary text-sm inline-flex items-center gap-1.5">
              <ExternalLink className="w-4 h-4" /> Preview
            </a>
          )}
          {tab !== 'card_payment' && tab !== 'customers' && (
            <button
              type="button"
              className="btn-primary text-sm inline-flex items-center gap-1.5"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(form)}
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          )}
        </header>

        <div className="px-4 pt-4 flex gap-2 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => selectTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap ${tab === t.id ? 'bg-teal-700 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {setupWarnings.length > 0 && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 space-y-1">
              <p className="font-semibold">Setup checklist</p>
              <ul className="list-disc pl-5 space-y-0.5">
                {setupWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {isLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : tab === 'general' ? (
            <div className="max-w-3xl space-y-4 bg-white border border-gray-200 rounded-xl p-5">
              {form.allow_web === false || form.allow_web === 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Public Store is not in your package.{' '}
                  <Link to="/support?subject=Enable%20Public%20Store%20(Web%20Integration)" className="font-medium underline">
                    Contact Support
                  </Link>{' '}
                  to purchase the Web add-on, or open it from Settings → Marketplaces.
                </div>
              ) : null}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!form.enable_storefront}
                  disabled={form.allow_web === false || form.allow_web === 0}
                  onChange={(e) => setForm({ ...form, enable_storefront: e.target.checked ? 1 : 0 })}
                />
                Enable public store
              </label>
              <div>
                <label className="text-sm font-medium">Shop display name</label>
                <input className="input-field mt-1" value={form.shop_display_name || ''} onChange={(e) => setForm({ ...form, shop_display_name: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.allow_delivery} onChange={(e) => setForm({ ...form, allow_delivery: e.target.checked ? 1 : 0 })} /> Allow delivery</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.allow_collection} onChange={(e) => setForm({ ...form, allow_collection: e.target.checked ? 1 : 0 })} /> Allow collection</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.pay_on_delivery} onChange={(e) => setForm({ ...form, pay_on_delivery: e.target.checked ? 1 : 0 })} /> Pay on delivery</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.pay_on_collection} onChange={(e) => setForm({ ...form, pay_on_collection: e.target.checked ? 1 : 0 })} /> Pay on collection</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.loyalty_on_storefront} onChange={(e) => setForm({ ...form, loyalty_on_storefront: e.target.checked ? 1 : 0 })} /> Loyalty on storefront</label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.show_prices_without_login !== 0 && form.show_prices_without_login !== false}
                    onChange={(e) => setForm({ ...form, show_prices_without_login: e.target.checked ? 1 : 0 })}
                  />
                  Show prices without login
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.auto_approve_customer_accounts !== 0 && form.auto_approve_customer_accounts !== false}
                    onChange={(e) =>
                      setForm({ ...form, auto_approve_customer_accounts: e.target.checked ? 1 : 0 })
                    }
                  />
                  Auto-approve customer signups
                </label>
              </div>
              {form.show_prices_without_login === 0 || form.show_prices_without_login === false ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  Guests can browse products but must log in to see prices and add items to the basket.
                </p>
              ) : null}
              {form.auto_approve_customer_accounts === 0 || form.auto_approve_customer_accounts === false ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  New store signups stay pending until you approve them under{' '}
                  <button type="button" className="font-medium underline" onClick={() => selectTab('customers')}>
                    Store customers
                  </button>
                  . You can also set a login and password on a POS customer so they can sign in.
                </p>
              ) : null}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Default delivery fee</label>
                  <input type="number" className="input-field mt-1" value={form.default_delivery_fee ?? 0} onChange={(e) => setForm({ ...form, default_delivery_fee: e.target.value })} />
                </div>
                <div>
                  <label className="text-sm font-medium">Minimum order</label>
                  <input type="number" className="input-field mt-1" value={form.min_order_amount ?? 0} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Collection instructions</label>
                <textarea className="input-field mt-1" rows={2} value={form.collection_instructions || ''} onChange={(e) => setForm({ ...form, collection_instructions: e.target.value })} />
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
                <p className="text-sm font-semibold text-slate-900">Online card payment</p>
                <p className="text-sm text-slate-700">
                  {activeCardGateway
                    ? <>Active gateway: <strong>{activeCardGateway}</strong>. Only one card provider can be on at a time.</>
                    : 'No online card gateway selected — customers pay on collection or delivery.'}
                </p>
                <button
                  type="button"
                  onClick={() => selectTab('card_payment')}
                  className="inline-flex text-sm font-medium text-slate-800 underline underline-offset-2"
                >
                  Open Card payment →
                </button>
              </div>
            </div>
          ) : tab === 'customers' ? (
            <StoreCustomersPanel />
          ) : tab === 'card_payment' ? (
            <StoreCardPaymentTab />
          ) : tab === 'theme' ? (
            <ThemePanel form={form} setForm={setForm} />
          ) : tab === 'catalogue' ? (
            <CataloguePanel
              form={form}
              setForm={setForm}
              catalogue={catalogue.data}
              isLoading={catalogue.isLoading}
              search={search}
              setSearch={setSearch}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              onRefresh={() => void catalogue.refetch()}
            />
          ) : tab === 'zones' ? (
            <ZonesPanel zones={zones.data || []} onSaved={() => void zones.refetch()} />
          ) : tab === 'pages' ? (
            <PagesPanel
              shopSeoTitle={form.seo_title || ''}
              shopSeoDescription={form.seo_description || ''}
              shopSeoKeywords={form.seo_keywords || ''}
              onShopSeoChange={(seo_title, seo_description, seo_keywords) =>
                setForm({ ...form, seo_title, seo_description, seo_keywords })
              }
              onSaveShopSeo={() => saveMutation.mutate(form)}
              savingShopSeo={saveMutation.isPending}
            />
          ) : (
            <DomainSharePanel
              form={form}
              setForm={setForm}
              publicUrl={publicUrl}
              shopName={shopDisplayName}
              onSave={() => saveMutation.mutateAsync(form)}
              onVerified={() => void qc.invalidateQueries({ queryKey: ['storefrontAdminSettings'] })}
            />
          )}
        </div>
      </div>
    </div>
  );
}

async function uploadStoreImage(file: File): Promise<string> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await saasApi.uploadFile(fd);
  return res.data.message.file_url as string;
}

function CataloguePanel({
  form,
  setForm,
  catalogue,
  isLoading,
  search,
  setSearch,
  categoryFilter,
  setCategoryFilter,
  onRefresh,
}: {
  form: Record<string, any>;
  setForm: (f: Record<string, any>) => void;
  catalogue: any;
  isLoading: boolean;
  search: string;
  setSearch: (v: string) => void;
  categoryFilter: string;
  setCategoryFilter: (v: string) => void;
  onRefresh: () => void;
}) {
  const customPricing = form.storefront_pricing_mode === 'custom';
  const allowDelivery = !!form.allow_delivery;
  const allowCollection = !!form.allow_collection;
  const [catOrder, setCatOrder] = useState<any[]>([]);
  const [dragCat, setDragCat] = useState<string | null>(null);

  useEffect(() => {
    setCatOrder(catalogue?.categories || []);
  }, [catalogue?.categories]);

  const publishedCats = catOrder.filter((c: any) => c.show_on_storefront);
  const items = catalogue?.items || [];

  const persistCatOrder = async (next: any[]) => {
    setCatOrder(next);
    try {
      await storefrontAdminApi.reorderCategories(next.map((c) => c.name));
      toast.success('Category order saved');
      onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
      onRefresh();
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b text-sm font-medium text-gray-700 flex items-center justify-between gap-2">
          <span>Categories — drag to sort · tick to publish</span>
          <span className="text-xs font-normal text-gray-500">{publishedCats.length} published</span>
        </div>
        <div className="divide-y max-h-56 overflow-y-auto">
          {catOrder.map((c: any, idx: number) => (
            <div
              key={c.name}
              draggable
              onDragStart={() => setDragCat(c.name)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (!dragCat || dragCat === c.name) return;
                const from = catOrder.findIndex((x) => x.name === dragCat);
                const to = idx;
                if (from < 0) return;
                const next = [...catOrder];
                const [moved] = next.splice(from, 1);
                next.splice(to, 0, moved);
                setDragCat(null);
                void persistCatOrder(next);
              }}
              className={`flex items-center gap-2 px-3 py-2 text-sm bg-white ${dragCat === c.name ? 'opacity-60' : ''}`}
            >
              <span className="text-gray-400 cursor-grab active:cursor-grabbing touch-none" title="Drag to reorder">
                <GripVertical className="w-4 h-4" />
              </span>
              <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!c.show_on_storefront}
                  onChange={async (e) => {
                    await storefrontAdminApi.setCategory({ category: c.name, show_on_storefront: e.target.checked ? 1 : 0 });
                    if (!e.target.checked && categoryFilter === c.name) setCategoryFilter('');
                    onRefresh();
                  }}
                />
                <span className="flex-1 truncate">{c.category_name}</span>
              </label>
              <button
                type="button"
                className="text-xs text-teal-700 shrink-0"
                onClick={async () => {
                  await storefrontAdminApi.bulkPublishCategory(c.name, 1);
                  toast.success('Category + products published');
                  onRefresh();
                }}
              >
                Publish all items
              </button>
            </div>
          ))}
          {!catOrder.length && <p className="px-4 py-3 text-sm text-gray-500">No categories yet.</p>}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs font-medium text-gray-600">Filter products by category</label>
            <select
              className="input-field mt-1"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All published categories</option>
              {publishedCats.map((c: any) => (
                <option key={c.name} value={c.name}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs font-medium text-gray-600">Search</label>
            <input
              className="input-field mt-1"
              placeholder="Search products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="min-w-[200px]">
            <label className="text-xs font-medium text-gray-600">Online pricing</label>
            <select
              className="input-field mt-1"
              value={form.storefront_pricing_mode || 'same_as_pos'}
              onChange={async (e) => {
                const mode = e.target.value;
                setForm({ ...form, storefront_pricing_mode: mode });
                try {
                  await storefrontAdminApi.saveSettings({ ...form, storefront_pricing_mode: mode });
                  toast.success(mode === 'custom' ? 'Custom online prices enabled' : 'Using POS prices online');
                } catch (err: any) {
                  toast.error(err.response?.data?.message || err.message);
                }
              }}
            >
              <option value="same_as_pos">Same as POS (sale + delivery)</option>
              <option value="custom">Custom online collection / delivery</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Product list shows only items in <strong>published</strong> categories
          {categoryFilter ? ' (filtered to the selected category)' : ''}.
          {allowDelivery
            ? ' Delivery price uses the POS 2nd price, or a custom online delivery price when enabled.'
            : ' Enable delivery under Shop & payments to set online delivery prices.'}
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">Publish</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">POS price</th>
                {allowDelivery && <th className="px-3 py-2">POS delivery</th>}
                {allowCollection && (
                  <th className="px-3 py-2">{customPricing ? 'Online collection' : 'Online (collection)'}</th>
                )}
                {allowDelivery && (
                  <th className="px-3 py-2">{customPricing ? 'Online delivery' : 'Online (delivery)'}</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((it: any) => (
                <tr key={it.name} className="align-middle">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={!!it.show_on_storefront}
                      onChange={async (e) => {
                        await storefrontAdminApi.setItem({ item: it.name, show_on_storefront: e.target.checked ? 1 : 0 });
                        onRefresh();
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">{it.item_name}</td>
                  <td className="px-3 py-2 text-gray-600">{it.category_name || '—'}</td>
                  <td className="px-3 py-2 tabular-nums">{it.sale_price ?? 0}</td>
                  {allowDelivery && (
                    <td className="px-3 py-2 tabular-nums text-gray-700">
                      {Number(it.delivery_price) > 0 ? it.delivery_price : <span className="text-gray-400">—</span>}
                    </td>
                  )}
                  {allowCollection && (
                    <td className="px-3 py-2">
                      {customPricing ? (
                        <input
                          key={`${it.name}-col-${it.storefront_price ?? ''}`}
                          className="input-field w-24"
                          type="number"
                          min={0}
                          step="0.01"
                          defaultValue={it.storefront_price ?? ''}
                          placeholder={String(it.sale_price ?? '')}
                          onBlur={async (e) => {
                            await storefrontAdminApi.setItem({ item: it.name, storefront_price: e.target.value });
                            toast.success('Collection price saved');
                            onRefresh();
                          }}
                        />
                      ) : (
                        <span className="tabular-nums text-gray-700">{it.sale_price ?? 0}</span>
                      )}
                    </td>
                  )}
                  {allowDelivery && (
                    <td className="px-3 py-2">
                      {customPricing ? (
                        <input
                          key={`${it.name}-del-${it.storefront_delivery_price ?? ''}`}
                          className="input-field w-24"
                          type="number"
                          min={0}
                          step="0.01"
                          defaultValue={it.storefront_delivery_price ?? ''}
                          placeholder={
                            Number(it.delivery_price) > 0
                              ? String(it.delivery_price)
                              : String(it.sale_price ?? '')
                          }
                          onBlur={async (e) => {
                            await storefrontAdminApi.setItem({
                              item: it.name,
                              storefront_delivery_price: e.target.value,
                            });
                            toast.success('Delivery price saved');
                            onRefresh();
                          }}
                        />
                      ) : (
                        <span className="tabular-nums text-gray-700">
                          {Number(it.delivery_price) > 0 ? it.delivery_price : it.sale_price ?? 0}
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    {publishedCats.length
                      ? 'No products in published categories yet — publish a category or use “Publish all items”.'
                      : 'Publish at least one category above to see products here.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ThemePanel({
  form,
  setForm,
}: {
  form: Record<string, any>;
  setForm: (f: Record<string, any>) => void;
}) {
  const primary = form.primary_colour || form.accent_colour || '#0F766E';
  const secondary = form.secondary_colour || '#134E4A';
  const link = form.link_colour || primary;
  const logo = form.store_logo || form.company_logo || '';
  const heroes: any[] = Array.isArray(form.hero_banners) ? form.hero_banners : [];
  const hero = heroes[0] || { image: '', title: '', subtitle: '', cta_label: '', cta_url: '', sort_order: 0, is_active: 1 };

  const setHero = (patch: Record<string, unknown>) => {
    const next = { ...hero, ...patch, is_active: 1, sort_order: 0 };
    const rest = heroes.slice(1);
    // Keep draft in form even without image yet; save API skips rows missing image
    const cleared = !next.image && !next.title && !next.subtitle && !next.cta_label;
    setForm({ ...form, hero_banners: cleared ? rest : [next, ...rest] });
  };

  const sections = [
    {
      key: 'show_hero_banner',
      title: 'Hero banner',
      blurb: 'Full-width homepage banner with title and call-to-action.',
    },
    {
      key: 'show_categories_carousel',
      title: 'Categories carousel',
      blurb: 'Horizontal scroll of published categories under the hero.',
    },
    {
      key: 'show_featured_products',
      title: 'Featured products',
      blurb: 'Product grid near the top of the homepage.',
    },
    {
      key: 'show_category_strips',
      title: 'Category product strips',
      blurb: 'Per-category product carousels on the homepage (works with all themes).',
    },
    {
      key: 'show_categories_in_footer',
      title: 'Categories in footer menu',
      blurb: 'List published categories in the shop footer for quick navigation.',
    },
  ] as const;

  const presets = [
    { id: 'modern', label: 'Modern', desc: 'Banners + category strips' },
    { id: 'hypermarket', label: 'Hypermarket', desc: 'Bold retail layout (HBK-style)' },
    { id: 'minimal', label: 'Minimal', desc: 'Simple clean product grid' },
  ] as const;

  return (
    <div className="max-w-5xl space-y-5">
      {/* Live preview strip */}
      <div
        className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm"
        style={{ ['--preview-primary' as string]: primary, ['--preview-secondary' as string]: secondary }}
      >
        <div className="bg-white px-4 py-3 flex items-center gap-3 border-b border-gray-100">
          {logo ? (
            <img
              src={resolveFileUrl(logo)}
              alt=""
              className="object-contain shrink-0"
              style={{ width: Number(form.logo_width) || 48, height: 'auto', maxHeight: Math.round((Number(form.logo_width) || 48) * 1.25) }}
            />
          ) : (
            <div className="w-9 h-9 bg-gray-100 flex items-center justify-center shrink-0">
              <Store className="w-4 h-4 text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {!!(form.show_shop_name_with_logo ?? 1) && (
              <p className="font-semibold text-gray-900 truncate text-sm">{form.shop_display_name || 'Your shop'}</p>
            )}
            <p className="text-xs text-gray-500">Homepage preview</p>
          </div>
          <span
            className="text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: form.button_bg_colour || primary,
              color: form.button_fg_colour || '#FFFFFF',
            }}
          >
            Basket
          </span>
        </div>
        {!!form.show_hero_banner && (
          <div
            className="relative h-28 sm:h-36 flex items-end justify-center sm:justify-start p-4 text-white"
            style={{
              background: hero.image
                ? `linear-gradient(to top, rgba(0,0,0,.5), transparent), url(${resolveFileUrl(hero.image)}) center/cover`
                : `linear-gradient(135deg, ${primary}, ${secondary})`,
            }}
          >
            <div>
              <p className="font-bold text-lg leading-tight">{hero.title || form.shop_display_name || 'Welcome'}</p>
              {(hero.subtitle || !hero.image) && (
                <p className="text-sm text-white/85 mt-0.5">{hero.subtitle || 'Order for delivery or collection'}</p>
              )}
            </div>
          </div>
        )}
        <div className="bg-gray-50 px-4 py-3 flex gap-2 overflow-hidden">
          {!!form.show_categories_carousel &&
            ['Dairy', 'Bakery', 'Fruit', 'Drinks'].map((c) => (
              <div key={c} className="shrink-0 w-16 text-center">
                <div className="w-12 h-12 mx-auto rounded-full" style={{ background: `${primary}22` }} />
                <p className="text-[10px] mt-1 text-gray-600">{c}</p>
              </div>
            ))}
          {!form.show_categories_carousel && (
            <p className="text-xs text-gray-400 py-2">Categories carousel hidden</p>
          )}
        </div>
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Links use <span style={{ color: link }} className="font-medium underline">link colour</span>
          </p>
          <button
            type="button"
            className="text-xs font-semibold px-3 py-1.5 rounded-lg"
            style={{
              background: form.button_bg_colour || primary,
              color: form.button_fg_colour || '#FFFFFF',
            }}
          >
            Add to basket
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Branding */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-teal-700" />
            <h3 className="font-semibold text-gray-900">Brand & colours</h3>
          </div>

          <div>
            <label className="text-sm font-medium">Store logo</label>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0">
                {logo ? (
                  <img src={resolveFileUrl(logo)} alt="" className="w-full h-full object-contain bg-white" />
                ) : (
                  <ImagePlus className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <label className="btn-secondary text-sm inline-flex items-center gap-1.5 cursor-pointer">
                  <Upload className="w-3.5 h-3.5" />
                  Upload logo
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const url = await uploadStoreImage(file);
                        setForm({ ...form, store_logo: url });
                        toast.success('Logo uploaded');
                      } catch {
                        toast.error('Logo upload failed');
                      }
                    }}
                  />
                </label>
                {logo && (
                  <button
                    type="button"
                    className="block text-xs text-red-600 mt-2"
                    onClick={() => setForm({ ...form, store_logo: '' })}
                  >
                    Remove logo
                  </button>
                )}
                <p className="text-xs text-gray-400 mt-1">PNG/JPG works best. Control size and name below.</p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Logo width (px)</label>
              <input
                type="number"
                min={24}
                max={160}
                className="input-field mt-1"
                value={form.logo_width ?? 48}
                onChange={(e) => setForm({ ...form, logo_width: Number(e.target.value) || 48 })}
              />
              <p className="text-xs text-gray-400 mt-1">Width only — height scales automatically (24–160).</p>
            </div>
            <div className="space-y-2 pt-1 sm:pt-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!(form.show_shop_name_with_logo ?? 1)}
                  onChange={(e) => setForm({ ...form, show_shop_name_with_logo: e.target.checked ? 1 : 0 })}
                />
                Show shop name next to logo
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!(form.show_logo_in_footer ?? 1)}
                  onChange={(e) => setForm({ ...form, show_logo_in_footer: e.target.checked ? 1 : 0 })}
                />
                Show logo in footer
              </label>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {(
              [
                ['primary_colour', 'Primary', primary],
                ['secondary_colour', 'Secondary', secondary],
                ['link_colour', 'Links', link],
              ] as const
            ).map(([key, label, value]) => (
              <div key={key}>
                <label className="text-xs font-medium text-gray-600">{label}</label>
                <div className="mt-1 flex items-center gap-1.5">
                  <input
                    type="color"
                    className="h-10 w-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
                    value={value}
                    onChange={(e) => {
                      const next = { ...form, [key]: e.target.value };
                      if (key === 'primary_colour') next.accent_colour = e.target.value;
                      setForm(next);
                    }}
                  />
                  <input
                    className="input-field text-xs font-mono flex-1 min-w-0"
                    value={value}
                    onChange={(e) => {
                      const next = { ...form, [key]: e.target.value };
                      if (key === 'primary_colour') next.accent_colour = e.target.value;
                      setForm(next);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">Buttons</p>
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  ['button_bg_colour', 'Background', form.button_bg_colour || primary],
                  ['button_fg_colour', 'Text (foreground)', form.button_fg_colour || '#FFFFFF'],
                  ['button_hover_bg_colour', 'Hover background', form.button_hover_bg_colour || secondary],
                  ['button_hover_fg_colour', 'Hover text', form.button_hover_fg_colour || '#FFFFFF'],
                ] as const
              ).map(([key, label, value]) => (
                <div key={key}>
                  <label className="text-xs font-medium text-gray-600">{label}</label>
                  <div className="mt-1 flex items-center gap-1.5">
                    <input
                      type="color"
                      className="h-10 w-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
                      value={value}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    />
                    <input
                      className="input-field text-xs font-mono flex-1 min-w-0"
                      value={value}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                className="text-sm font-semibold px-4 py-2 rounded-lg transition"
                style={{
                  background: form.button_bg_colour || primary,
                  color: form.button_fg_colour || '#FFFFFF',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = form.button_hover_bg_colour || secondary;
                  e.currentTarget.style.color = form.button_hover_fg_colour || '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = form.button_bg_colour || primary;
                  e.currentTarget.style.color = form.button_fg_colour || '#FFFFFF';
                }}
              >
                Preview button
              </button>
              <span className="text-xs text-gray-400">Hover to check colours</span>
            </div>
          </div>

          <p className="text-xs text-gray-500">Primary / secondary colour the chrome; button colours control Add to basket, Log in, etc.</p>
        </div>

        {/* Layout preset */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-gray-900">Layout preset</h3>
          <div className="grid gap-2">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setForm({ ...form, theme: p.id })}
                className={`text-left rounded-xl border px-4 py-3 transition ${
                  (form.theme === 'fresh' ? 'modern' : form.theme || 'modern') === p.id
                    ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="font-medium text-gray-900 text-sm">{p.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header / footer copy */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">Header &amp; footer text</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Edit every customer-facing label — nothing on the storefront should stay hard-coded.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.show_announcement_bar}
            onChange={(e) => setForm({ ...form, show_announcement_bar: e.target.checked ? 1 : 0 })}
          />
          Show top announcement bar
        </label>
        <div>
          <label className="text-sm font-medium">Announcement bar text</label>
          <input
            className="input-field mt-1"
            value={form.announcement_text || ''}
            onChange={(e) => setForm({ ...form, announcement_text: e.target.value })}
            placeholder="Order online for delivery or collection · UK delivery zones available"
            disabled={!form.show_announcement_bar}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Header subtitle</label>
            <input
              className="input-field mt-1"
              value={form.header_subtitle || ''}
              onChange={(e) => setForm({ ...form, header_subtitle: e.target.value })}
              placeholder="Online shop"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Search placeholder</label>
            <input
              className="input-field mt-1"
              value={form.search_placeholder || ''}
              onChange={(e) => setForm({ ...form, search_placeholder: e.target.value })}
              placeholder="Search products…"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Basket button label</label>
            <input
              className="input-field mt-1"
              value={form.basket_label || ''}
              onChange={(e) => setForm({ ...form, basket_label: e.target.value })}
              placeholder="Basket"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Login button label</label>
            <input
              className="input-field mt-1"
              value={form.login_label || ''}
              onChange={(e) => setForm({ ...form, login_label: e.target.value })}
              placeholder="Log in"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Footer tagline</label>
          <textarea
            className="input-field mt-1"
            rows={2}
            value={form.footer_tagline || ''}
            onChange={(e) => setForm({ ...form, footer_tagline: e.target.value })}
            placeholder="Order online for delivery or collection…"
          />
        </div>
      </div>

      {/* Product browsing — category / brand / model / mix */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">Product browsing</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Let shoppers browse by category, brand, model — or any mix. Brand/model only appear when your
            published products are linked to them in Items.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {(
            [
              {
                key: 'browse_by_category',
                title: 'By category',
                blurb: 'Shop by department / product category.',
              },
              {
                key: 'browse_by_brand',
                title: 'By brand',
                blurb: 'Shop by brand (products must have a Brand set).',
              },
              {
                key: 'browse_by_model',
                title: 'By model',
                blurb: 'Shop by model (products must have a Model set).',
              },
            ] as const
          ).map((s) => (
            <label
              key={s.key}
              className={`flex gap-3 rounded-xl border p-4 cursor-pointer transition ${
                form[s.key] ? 'border-teal-200 bg-teal-50/40' : 'border-gray-200 bg-white'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={!!form[s.key]}
                onChange={(e) => {
                  const next = { ...form, [s.key]: e.target.checked ? 1 : 0 };
                  if (!next.browse_by_category && !next.browse_by_brand && !next.browse_by_model) {
                    next.browse_by_category = 1;
                  }
                  setForm(next);
                }}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{s.title}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{s.blurb}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {!!form.browse_by_brand && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.show_brands_carousel !== 0 && form.show_brands_carousel !== false}
                onChange={(e) => setForm({ ...form, show_brands_carousel: e.target.checked ? 1 : 0 })}
              />
              Show brands on homepage
            </label>
          )}
          {!!form.browse_by_model && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.show_models_carousel !== 0 && form.show_models_carousel !== false}
                onChange={(e) => setForm({ ...form, show_models_carousel: e.target.checked ? 1 : 0 })}
              />
              Show models on homepage
            </label>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Mix tip: enable Category + Brand so visitors can open a category and refine by brand (and the other
          way around).
        </p>
      </div>

      {/* Homepage sections */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">Homepage sections</h3>
          <p className="text-sm text-gray-500 mt-0.5">Turn blocks on or off — like a simple website builder.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {sections.map((s) => (
            <label
              key={s.key}
              className={`flex gap-3 rounded-xl border p-4 cursor-pointer transition ${
                form[s.key] ? 'border-teal-200 bg-teal-50/40' : 'border-gray-200 bg-white'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={!!form[s.key]}
                onChange={(e) => setForm({ ...form, [s.key]: e.target.checked ? 1 : 0 })}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{s.title}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{s.blurb}</span>
              </span>
            </label>
          ))}
        </div>
        {!!form.show_featured_products && (
          <div className="max-w-md">
            <label className="text-sm font-medium">Featured section title</label>
            <input
              className="input-field mt-1"
              value={form.featured_section_title || ''}
              onChange={(e) => setForm({ ...form, featured_section_title: e.target.value })}
              placeholder="Popular products"
            />
          </div>
        )}
      </div>

      {/* Hero editor */}
      <div className={`bg-white border border-gray-200 rounded-xl p-5 space-y-4 ${!form.show_hero_banner ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">Hero banner content</h3>
            <p className="text-sm text-gray-500 mt-0.5">Optional image, headline and button for the top of the shop.</p>
          </div>
          {!form.show_hero_banner && (
            <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full">Section off</span>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-40 h-24 rounded-xl border border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center shrink-0 relative">
            {hero.image ? (
              <img src={resolveFileUrl(hero.image)} alt="" className="w-full h-full object-cover" />
            ) : (
              <ImagePlus className="w-6 h-6 text-gray-300" />
            )}
          </div>
          <div className="flex-1 min-w-[200px] space-y-2">
            <label className="btn-secondary text-sm inline-flex items-center gap-1.5 cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              {hero.image ? 'Replace image' : 'Upload hero image'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const url = await uploadStoreImage(file);
                    setHero({ image: url });
                    toast.success('Hero image uploaded');
                  } catch {
                    toast.error('Upload failed');
                  }
                }}
              />
            </label>
            {hero.image && (
              <button type="button" className="block text-xs text-red-600" onClick={() => setHero({ image: '' })}>
                Remove image
              </button>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Headline</label>
            <input className="input-field mt-1" value={hero.title || ''} onChange={(e) => setHero({ title: e.target.value })} placeholder="Fresh deals this week" />
          </div>
          <div>
            <label className="text-sm font-medium">Subtitle</label>
            <input className="input-field mt-1" value={hero.subtitle || ''} onChange={(e) => setHero({ subtitle: e.target.value })} placeholder="Order before 2pm for same-day delivery" />
          </div>
          <div>
            <label className="text-sm font-medium">Button label</label>
            <input className="input-field mt-1" value={hero.cta_label || ''} onChange={(e) => setHero({ cta_label: e.target.value })} placeholder="Shop now" />
          </div>
          <div>
            <label className="text-sm font-medium">Button link</label>
            <input className="input-field mt-1" value={hero.cta_url || ''} onChange={(e) => setHero({ cta_url: e.target.value })} placeholder="/" />
          </div>
        </div>
      </div>
    </div>
  );
}

function PagesPanel({
  shopSeoTitle,
  shopSeoDescription,
  shopSeoKeywords,
  onShopSeoChange,
  onSaveShopSeo,
  savingShopSeo,
}: {
  shopSeoTitle: string;
  shopSeoDescription: string;
  shopSeoKeywords: string;
  onShopSeoChange: (title: string, desc: string, keywords: string) => void;
  onSaveShopSeo: () => void;
  savingShopSeo: boolean;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<PageDraft>(emptyPage());
  const [editing, setEditing] = useState(false);
  const [pageOrder, setPageOrder] = useState<any[]>([]);
  const [dragPage, setDragPage] = useState<string | null>(null);

  const pages = useQuery({
    queryKey: ['storefrontPages'],
    queryFn: async () => (await storefrontAdminApi.listPages()).data.message as any[],
  });

  useEffect(() => {
    setPageOrder(pages.data || []);
  }, [pages.data]);

  const persistPageOrder = async (next: any[]) => {
    setPageOrder(next);
    try {
      await storefrontAdminApi.reorderPages(next.map((p) => p.name));
      toast.success('Menu order saved');
      void pages.refetch();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
      void pages.refetch();
    }
  };

  const openPage = async (name: string) => {
    const res = await storefrontAdminApi.getPage(name);
    const p = res.data.message;
    setDraft({
      name: p.name,
      title: p.title || '',
      slug: p.slug || '',
      page_type: p.page_type || 'custom',
      is_published: p.is_published ? 1 : 0,
      show_in_footer: p.show_in_footer ? 1 : 0,
      show_in_header: p.show_in_header ? 1 : 0,
      show_contact_form: p.show_contact_form ? 1 : 0,
      content: p.content || '',
      seo_title: p.seo_title || '',
      seo_description: p.seo_description || '',
      seo_keywords: p.seo_keywords || '',
      og_image: p.og_image || '',
      hero_enabled: p.hero_enabled ? 1 : 0,
      hero_image: p.hero_image || '',
      hero_title: p.hero_title || '',
      hero_subtitle: p.hero_subtitle || '',
      hero_cta_label: p.hero_cta_label || '',
      hero_cta_url: p.hero_cta_url || '',
      sort_order: p.sort_order || 0,
    });
    setSelected(name);
    setEditing(true);
  };

  const savePage = async () => {
    if (!draft.title.trim()) return toast.error('Title is required');
    try {
      const res = await storefrontAdminApi.savePage(draft);
      toast.success('Page saved');
      setSelected(res.data.message.name);
      setDraft((d) => ({ ...d, name: res.data.message.name, slug: res.data.message.slug }));
      void pages.refetch();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col min-h-[420px]">
        <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-800">Pages</p>
            <p className="text-[11px] text-gray-400">Drag to set menu order</p>
          </div>
          <button
            type="button"
            className="btn-secondary text-xs inline-flex items-center gap-1"
            onClick={() => {
              setDraft(emptyPage());
              setSelected(null);
              setEditing(true);
            }}
          >
            <Plus className="w-3.5 h-3.5" /> New
          </button>
        </div>
        <div className="flex-1 overflow-y-auto divide-y">
          {pageOrder.map((p, idx) => (
            <div
              key={p.name}
              draggable
              onDragStart={() => setDragPage(p.name)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (!dragPage || dragPage === p.name) return;
                const from = pageOrder.findIndex((x) => x.name === dragPage);
                if (from < 0) return;
                const next = [...pageOrder];
                const [moved] = next.splice(from, 1);
                next.splice(idx, 0, moved);
                setDragPage(null);
                void persistPageOrder(next);
              }}
              className={`flex items-stretch gap-1 ${selected === p.name ? 'bg-teal-50' : 'hover:bg-gray-50'} ${dragPage === p.name ? 'opacity-60' : ''}`}
            >
              <span
                className="px-1.5 flex items-center text-gray-400 cursor-grab active:cursor-grabbing touch-none"
                title="Drag to reorder"
              >
                <GripVertical className="w-4 h-4" />
              </span>
              <button
                type="button"
                onClick={() => void openPage(p.name)}
                className="flex-1 text-left px-2 py-2.5 text-sm min-w-0"
              >
                <div className="font-medium text-gray-900 truncate">{p.title}</div>
                <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                  <span>/{p.slug}</span>
                  <span className={p.is_published ? 'text-emerald-600' : 'text-amber-600'}>
                    {p.is_published ? 'Published' : 'Draft'}
                  </span>
                  {!!p.show_in_header && <span className="text-teal-700">Header</span>}
                  {!!p.show_in_footer && <span className="text-slate-500">Footer</span>}
                </div>
              </button>
            </div>
          ))}
          {!pageOrder.length && !pages.isLoading && (
            <p className="p-4 text-sm text-gray-500">No pages yet — click New or open defaults.</p>
          )}
        </div>
        <div className="border-t p-3 space-y-2 bg-gray-50">
          <p className="text-xs font-medium text-gray-600">Shop-wide SEO (homepage)</p>
          <input
            className="input-field text-sm"
            placeholder="Homepage SEO title"
            value={shopSeoTitle}
            onChange={(e) => onShopSeoChange(e.target.value, shopSeoDescription, shopSeoKeywords)}
          />
          <textarea
            className="input-field text-sm"
            rows={2}
            placeholder="Homepage SEO description"
            value={shopSeoDescription}
            onChange={(e) => onShopSeoChange(shopSeoTitle, e.target.value, shopSeoKeywords)}
          />
          <input
            className="input-field text-sm"
            placeholder="Keywords (comma separated)"
            value={shopSeoKeywords}
            onChange={(e) => onShopSeoChange(shopSeoTitle, shopSeoDescription, e.target.value)}
          />
          <button type="button" className="btn-secondary text-xs w-full" disabled={savingShopSeo} onClick={onSaveShopSeo}>
            Save homepage SEO
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 min-h-[420px]">
        {!editing ? (
          <div className="h-full grid place-items-center text-center text-gray-500 text-sm px-6">
            <div>
              <p className="font-medium text-gray-800 mb-1">Create rich store pages</p>
              <p>Each page has its own content, SEO tags, and an optional hero banner — About, Terms, Privacy, or any custom page.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <h3 className="font-semibold text-gray-900">{draft.name ? 'Edit page' : 'New page'}</h3>
              <div className="flex gap-2">
                {draft.name && draft.page_type === 'custom' && (
                  <button
                    type="button"
                    className="btn-secondary text-xs text-red-600 inline-flex items-center gap-1"
                    onClick={async () => {
                      await storefrontAdminApi.deletePage(draft.name!);
                      toast.success('Page deleted');
                      setEditing(false);
                      setSelected(null);
                      void pages.refetch();
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                )}
                <button type="button" className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={() => void savePage()}>
                  <Save className="w-4 h-4" /> Save page
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Title</label>
                <input
                  className="input-field mt-1"
                  value={draft.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setDraft((d) => ({
                      ...d,
                      title,
                      slug: d.name ? d.slug : title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                    }));
                  }}
                />
              </div>
              <div>
                <label className="text-sm font-medium">URL slug</label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-gray-400">/store/</span>
                  <input className="input-field" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!draft.is_published} onChange={(e) => setDraft({ ...draft, is_published: e.target.checked ? 1 : 0 })} />
                Published
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!draft.show_in_footer} onChange={(e) => setDraft({ ...draft, show_in_footer: e.target.checked ? 1 : 0 })} />
                Show in footer
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!draft.show_in_header} onChange={(e) => setDraft({ ...draft, show_in_header: e.target.checked ? 1 : 0 })} />
                Show in header menu
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!draft.show_contact_form} onChange={(e) => setDraft({ ...draft, show_contact_form: e.target.checked ? 1 : 0 })} />
                Show contact form
              </label>
            </div>
            {!!draft.show_contact_form && (
              <p className="text-xs text-gray-500 -mt-2">
                Adds a fixed form (Name, Email, Phone, Message) below the page content. Submissions are emailed to the shop owner email (footer email / company email).
              </p>
            )}

            <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-800">SEO for this page</p>
              <input className="input-field" placeholder="SEO title" value={draft.seo_title} onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })} />
              <textarea className="input-field" rows={2} placeholder="SEO description" value={draft.seo_description} onChange={(e) => setDraft({ ...draft, seo_description: e.target.value })} />
              <input className="input-field" placeholder="Keywords (comma separated)" value={draft.seo_keywords} onChange={(e) => setDraft({ ...draft, seo_keywords: e.target.value })} />
              <ImageUrlField
                label="Social / OG image"
                value={draft.og_image}
                onChange={(og_image) => setDraft({ ...draft, og_image })}
                hint="Upload or paste a URL for social sharing previews."
              />
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <input type="checkbox" checked={!!draft.hero_enabled} onChange={(e) => setDraft({ ...draft, hero_enabled: e.target.checked ? 1 : 0 })} />
                Optional hero banner
              </label>
              {!!draft.hero_enabled && (
                <>
                  <ImageUrlField
                    label="Hero image"
                    value={draft.hero_image}
                    onChange={(hero_image) => setDraft({ ...draft, hero_image })}
                    hint="Upload or paste image URL."
                  />
                  <input className="input-field" placeholder="Hero title" value={draft.hero_title} onChange={(e) => setDraft({ ...draft, hero_title: e.target.value })} />
                  <input className="input-field" placeholder="Hero subtitle" value={draft.hero_subtitle} onChange={(e) => setDraft({ ...draft, hero_subtitle: e.target.value })} />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input className="input-field" placeholder="CTA label" value={draft.hero_cta_label} onChange={(e) => setDraft({ ...draft, hero_cta_label: e.target.value })} />
                    <input className="input-field" placeholder="CTA link" value={draft.hero_cta_url} onChange={(e) => setDraft({ ...draft, hero_cta_url: e.target.value })} />
                  </div>
                </>
              )}
            </div>

            <div>
              <label className="text-sm font-medium">Page content</label>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">Rich text editor (TinyMCE free / GPL) — format text, lists, links and images.</p>
              <RichTextEditor
                value={draft.content}
                onChange={(content) => setDraft({ ...draft, content })}
                height={360}
                placeholder="Write your page content…"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShopQrCard({ publicUrl, shopName }: { publicUrl: string; shopName: string }) {
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);

  const buildBrandedPng = async (): Promise<string> => {
    if (!publicUrl) throw new Error('No shop URL');
    const qrDataUrl = await QRCode.toDataURL(publicUrl, {
      width: 420,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#0f172a', light: '#ffffff' },
    });
    const W = 640;
    const H = 820;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#0f766e';
    ctx.fillRect(0, 0, W, 110);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px "Segoe UI", Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    const title = (shopName || 'Online shop').slice(0, 42);
    ctx.fillText(title, W / 2, 68);

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = qrDataUrl;
    });
    const qrSize = 420;
    const qrX = (W - qrSize) / 2;
    const qrY = 160;
    ctx.drawImage(img, qrX, qrY, qrSize, qrSize);

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 28px "Segoe UI", Helvetica, Arial, sans-serif';
    ctx.fillText('Scan to visit our online shop', W / 2, qrY + qrSize + 56);

    ctx.fillStyle = '#64748b';
    ctx.font = '18px "Segoe UI", Helvetica, Arial, sans-serif';
    const host = publicUrl
      .replace(/^https?:\/\//, '')
      .replace(/\/store\/?$/i, '')
      .replace(/\/$/, '');
    ctx.fillText(host.slice(0, 48), W / 2, qrY + qrSize + 92);

    return canvas.toDataURL('image/png');
  };

  useEffect(() => {
    let cancelled = false;
    if (!publicUrl) {
      setPreview('');
      return;
    }
    void (async () => {
      try {
        const url = await buildBrandedPng();
        if (!cancelled) setPreview(url);
      } catch {
        if (!cancelled) setPreview('');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicUrl, shopName]);

  const download = async () => {
    if (!publicUrl) return;
    setBusy(true);
    try {
      const dataUrl = preview || (await buildBrandedPng());
      const a = document.createElement('a');
      const safe = (shopName || 'shop').replace(/[^\w\-]+/g, '-').slice(0, 40);
      a.href = dataUrl;
      a.download = `${safe || 'shop'}-online-shop-qr.png`;
      a.click();
      toast.success('QR downloaded');
    } catch (err: any) {
      toast.error(err?.message || 'Could not generate QR');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid sm:grid-cols-[1fr_auto] gap-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <QrCode className="w-5 h-5 text-teal-700" />
          <h4 className="font-semibold text-gray-900">QR for your shop window</h4>
        </div>
        <p className="text-sm text-gray-500">
          Download includes your shop name and “Scan to visit our online shop” — print for the counter or window.
        </p>
        {publicUrl ? (
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            disabled={busy}
            onClick={() => void download()}
          >
            <Download className="w-4 h-4" />
            {busy ? 'Preparing…' : 'Download QR'}
          </button>
        ) : (
          <p className="text-xs text-amber-700">Save &amp; enable your store to generate a QR.</p>
        )}
      </div>
      {preview && (
        <div className="bg-white border border-gray-200 rounded-xl p-3 flex items-center justify-center max-w-[200px]">
          <img src={preview} alt="Shop QR preview" className="w-full h-auto rounded-lg" />
        </div>
      )}
    </div>
  );
}

/** Ensure share/open/QR always hit the storefront SPA at /store. */
function normalizeStorefrontUrl(raw: string): string {
  const value = String(raw || '').trim();
  if (!value) return '';
  let url = value;
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url.replace(/^\/+/, '')}`;
  }
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '') || '';
    if (path === '/store' || path.startsWith('/store/')) {
      u.pathname = '/store/';
    } else {
      u.pathname = '/store/';
    }
    u.search = '';
    u.hash = '';
    return u.toString();
  } catch {
    const base = url.replace(/\/+$/, '').replace(/\/store$/i, '');
    return `${base}/store/`;
  }
}

function DomainSharePanel({
  form,
  setForm,
  publicUrl,
  shopName,
  onSave,
  onVerified,
}: {
  form: Record<string, any>;
  setForm: (f: Record<string, any>) => void;
  publicUrl: string;
  shopName: string;
  onSave: () => Promise<unknown>;
  onVerified: () => void;
}) {
  const status = form.domain_status || 'Pending';
  const platformBase = form.platform_base_domain || 'saledesk.co.uk';
  const cnameTarget = form.cname_target || `shops.${platformBase}`;
  const subdomainHint = form.subdomain ? `${form.subdomain}.${platformBase}` : `yourshop.${platformBase}`;
  const activeHost =
    form.active_store_host ||
    (publicUrl ? publicUrl.replace(/^https?:\/\//i, '').replace(/\/store\/?$/i, '').replace(/\/$/, '') : platformBase);
  const usingCustom = Boolean(form.using_custom_domain) || status === 'Active';

  return (
    <div className="max-w-3xl space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-teal-800 to-slate-900 text-white p-6 sm:p-8">
        <p className="text-teal-100 text-sm font-medium">
          Your online shop · {activeHost}
          {usingCustom ? ' (custom domain)' : ''}
        </p>
        <h3 className="text-2xl font-bold mt-1 break-all">{publicUrl || 'Enable the store to get a public link'}</h3>
        <p className="text-teal-100/90 text-sm mt-2">
          Copy link and Open shop use the storefront at <code className="text-teal-50">/store</code>
          {usingCustom
            ? ' on your connected custom domain.'
            : ` on your current domain (e.g. https://${subdomainHint}/store/).`}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 bg-white text-teal-900 font-semibold text-sm px-4 py-2 rounded-xl"
            disabled={!publicUrl}
            onClick={() => {
              void navigator.clipboard.writeText(publicUrl);
              toast.success('Shop link copied');
            }}
          >
            <Copy className="w-4 h-4" /> Copy link
          </button>
          {publicUrl && (
            <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-white/10 border border-white/30 text-sm px-4 py-2 rounded-xl">
              <ExternalLink className="w-4 h-4" /> Open shop
            </a>
          )}
        </div>
      </div>

      <ShopQrCard publicUrl={publicUrl} shopName={shopName} />

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
            <Globe className="w-5 h-5 text-teal-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900">Use your own domain</h4>
            <p className="text-sm text-gray-500 mt-0.5">e.g. www.yourshop.co.uk — looks more professional than a subdomain.</p>
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${status === 'Active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>
            {status === 'Active' ? 'Connected' : 'Not connected'}
          </span>
        </div>

        <ol className="text-sm text-gray-600 space-y-2 list-decimal pl-5">
          <li>Enter the domain you own below.</li>
          <li>
            At your DNS provider, add a <strong>CNAME</strong> pointing to{' '}
            <code className="bg-gray-100 px-1 rounded text-xs">{cnameTarget}</code>
          </li>
          <li>Click Verify — we activate HTTPS when DNS is ready.</li>
        </ol>

        <div>
          <label className="text-sm font-medium">Custom domain</label>
          <input
            className="input-field mt-1"
            placeholder="www.myshop.co.uk"
            value={form.custom_domain || ''}
            onChange={(e) => setForm({ ...form, custom_domain: e.target.value })}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            onClick={async () => {
              await onSave();
              await storefrontAdminApi.verifyDomain();
              toast.success('Domain verified');
              onVerified();
            }}
          >
            <CheckCircle2 className="w-4 h-4" /> Verify domain
          </button>
          <button type="button" className="btn-secondary text-sm" onClick={() => void onSave()}>
            Save domain
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Amazon, eBay and developer API keys are managed under <strong>Settings → Marketplaces</strong> — not here.
      </p>
    </div>
  );
}

type ZoneDraft = {
  name?: string;
  zone_name: string;
  zone_type: 'postcode' | 'polygon';
  outward_postcodes: string;
  geojson: string;
  delivery_fee: number;
  min_order_amount: number;
  is_active: number;
};

const emptyZone = (): ZoneDraft => ({
  zone_name: '',
  zone_type: 'postcode',
  outward_postcodes: '',
  geojson: '',
  delivery_fee: 0,
  min_order_amount: 0,
  is_active: 1,
});

function zoneFromRow(z: any): ZoneDraft {
  const zt = String(z.zone_type || '').toLowerCase();
  const isPolygon =
    zt === 'polygon' || (!!(z.geojson || '').trim() && !(z.outward_postcodes || '').trim());
  return {
    name: z.name,
    zone_name: z.zone_name || '',
    zone_type: isPolygon ? 'polygon' : 'postcode',
    outward_postcodes: z.outward_postcodes || '',
    geojson: z.geojson || '',
    delivery_fee: Number(z.delivery_fee) || 0,
    min_order_amount: Number(z.min_order_amount) || 0,
    is_active: z.is_active ? 1 : 0,
  };
}

function ZonesPanel({ zones, onSaved }: { zones: any[]; onSaved: () => void }) {
  const [draft, setDraft] = useState<ZoneDraft>(emptyZone());
  const [saving, setSaving] = useState(false);
  const editing = !!draft.name;

  const reset = () => setDraft(emptyZone());

  const save = async () => {
    if (!draft.zone_name.trim()) return toast.error('Zone name is required');
    if (draft.zone_type === 'postcode' && !draft.outward_postcodes.trim()) {
      return toast.error('Enter outward postcodes (e.g. PR1, PR2)');
    }
    if (draft.zone_type === 'polygon' && !draft.geojson.trim()) {
      return toast.error('Draw a polygon on the map (3+ points)');
    }
    setSaving(true);
    try {
      await storefrontAdminApi.saveZone({
        name: draft.name,
        zone_name: draft.zone_name.trim(),
        zone_type: draft.zone_type,
        outward_postcodes: draft.zone_type === 'postcode' ? draft.outward_postcodes : '',
        geojson: draft.zone_type === 'polygon' ? draft.geojson : '',
        delivery_fee: draft.delivery_fee,
        min_order_amount: draft.min_order_amount,
        is_active: draft.is_active,
      });
      toast.success(editing ? 'Zone updated' : 'Zone added');
      reset();
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900">{editing ? 'Edit delivery zone' : 'Add delivery zone'}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Choose postcode coverage or draw a map area. Customers match by postcode (or location inside the polygon).
            </p>
          </div>
          {editing && (
            <button type="button" className="btn-secondary text-xs" onClick={reset}>
              Cancel edit
            </button>
          )}
        </div>

        <div>
          <label className="text-sm font-medium">Zone name</label>
          <input
            className="input-field mt-1"
            placeholder="e.g. Town centre"
            value={draft.zone_name}
            onChange={(e) => setDraft({ ...draft, zone_name: e.target.value })}
          />
        </div>

        <div>
          <label className="text-sm font-medium">Coverage type</label>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setDraft({ ...draft, zone_type: 'postcode' })}
              className={`text-left rounded-xl border px-4 py-3 transition ${
                draft.zone_type === 'postcode'
                  ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="text-sm font-medium text-gray-900">Postcode based</p>
              <p className="text-xs text-gray-500 mt-0.5">UK outward codes like PR1, PR2</p>
            </button>
            <button
              type="button"
              onClick={() => setDraft({ ...draft, zone_type: 'polygon' })}
              className={`text-left rounded-xl border px-4 py-3 transition ${
                draft.zone_type === 'polygon'
                  ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                <MapIcon className="w-3.5 h-3.5" /> Map polygon
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Draw the delivery area on a map</p>
            </button>
          </div>
        </div>

        {draft.zone_type === 'postcode' ? (
          <div>
            <label className="text-sm font-medium">Outward postcodes</label>
            <input
              className="input-field mt-1"
              placeholder="PR1, PR2, PR3"
              value={draft.outward_postcodes}
              onChange={(e) => setDraft({ ...draft, outward_postcodes: e.target.value })}
            />
            <p className="text-xs text-gray-400 mt-1">Comma-separated outward codes (letters + first digit group).</p>
          </div>
        ) : (
          <div>
            <label className="text-sm font-medium">Map area</label>
            <div className="mt-2">
              <ZonePolygonMap
                key={draft.name || 'new-zone'}
                value={draft.geojson}
                onChange={(geojson) => setDraft((d) => ({ ...d, geojson }))}
              />
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Delivery fee</label>
            <input
              type="number"
              min={0}
              step="0.01"
              className="input-field mt-1"
              value={draft.delivery_fee}
              onChange={(e) => setDraft({ ...draft, delivery_fee: Number(e.target.value) })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Minimum order</label>
            <input
              type="number"
              min={0}
              step="0.01"
              className="input-field mt-1"
              value={draft.min_order_amount}
              onChange={(e) => setDraft({ ...draft, min_order_amount: Number(e.target.value) })}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: e.target.checked ? 1 : 0 })}
          />
          Active (customers can match this zone)
        </label>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary text-sm inline-flex items-center gap-1.5" disabled={saving} onClick={() => void save()}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editing ? 'Update zone' : 'Add zone'}
          </button>
          {editing && (
            <button type="button" className="btn-secondary text-sm" onClick={reset}>
              New zone
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl divide-y">
        {zones.map((z) => {
          const row = zoneFromRow(z);
          const isEditing = draft.name === z.name;
          return (
            <div key={z.name} className={`px-4 py-3 flex items-start gap-3 text-sm ${isEditing ? 'bg-teal-50/50' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-gray-900">{z.zone_name}</p>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {row.zone_type === 'polygon' ? 'Map polygon' : 'Postcode'}
                  </span>
                  {!z.is_active && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Inactive</span>
                  )}
                </div>
                <p className="text-gray-500 mt-0.5">
                  {row.zone_type === 'polygon'
                    ? 'Drawn map area'
                    : z.outward_postcodes || 'No postcodes'}{' '}
                  · fee {z.delivery_fee} · min {z.min_order_amount}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  className="text-teal-700 text-xs inline-flex items-center gap-1"
                  onClick={() => {
                    setDraft(zoneFromRow(z));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  type="button"
                  className="text-red-600 text-xs inline-flex items-center gap-1"
                  onClick={async () => {
                    if (!window.confirm(`Delete zone “${z.zone_name}”?`)) return;
                    await storefrontAdminApi.deleteZone(z.name);
                    if (draft.name === z.name) reset();
                    toast.success('Zone deleted');
                    onSaved();
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          );
        })}
        {!zones.length && <p className="p-4 text-gray-500 text-sm">No zones yet.</p>}
      </div>
    </div>
  );
}


function StoreCustomersPanel() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('Pending');
  const [search, setSearch] = useState('');
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['storefrontStoreCustomers', status, search],
    queryFn: async () =>
      (
        await storefrontAdminApi.listStoreCustomers({
          status: status || undefined,
          search: search || undefined,
          limit: 80,
        })
      ).data.message as {
        customers: Array<Record<string, any>>;
        pending_count: number;
      },
  });

  const approveMut = useMutation({
    mutationFn: ({ name, next }: { name: string; next: string }) =>
      storefrontAdminApi.setStoreCustomerApproval(name, next),
    onSuccess: async (_res, vars) => {
      toast.success(vars.next === 'Approved' ? 'Customer approved' : `Marked ${vars.next}`);
      await refetch();
      void qc.invalidateQueries({ queryKey: ['storefrontStoreCustomers'] });
    },
    onError: (err: any) => toast.error(err.response?.data?.message || err.message),
  });

  const rows = data?.customers || [];

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Store customer accounts</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Approve public signups, or open a POS customer and set their store email and password.
            {typeof data?.pending_count === 'number' ? (
              <span className="ml-1 text-amber-800 font-medium">{data.pending_count} pending</span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {['Pending', 'Approved', 'Rejected', 'All'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                status === s ? 'bg-teal-700 text-white' : 'bg-gray-50 border border-gray-200 text-gray-700'
              }`}
            >
              {s}
            </button>
          ))}
          <input
            className="input-field text-sm flex-1 min-w-[10rem]"
            placeholder="Search name, email, mobile…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600 border-b">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">POS link</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((c) => (
                  <tr key={c.name} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{c.full_name}</div>
                      <div className="text-xs text-gray-500">{c.email}</div>
                      {c.mobile ? <div className="text-xs text-gray-400">{c.mobile}</div> : null}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.approval_status === 'Pending'
                            ? 'bg-amber-100 text-amber-800'
                            : c.approval_status === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {c.approval_status || 'Approved'}
                        {!c.is_active ? ' · inactive' : ''}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {c.pos_customer ? (
                        <Link className="text-teal-700 hover:underline" to={`/customers/${encodeURIComponent(c.pos_customer)}`}>
                          {c.pos_customer}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5 flex-wrap">
                        {c.approval_status !== 'Approved' && (
                          <button
                            type="button"
                            className="btn-primary text-xs px-2.5 py-1"
                            disabled={approveMut.isPending}
                            onClick={() => approveMut.mutate({ name: c.name, next: 'Approved' })}
                          >
                            Approve
                          </button>
                        )}
                        {c.approval_status !== 'Rejected' && (
                          <button
                            type="button"
                            className="btn-secondary text-xs px-2.5 py-1"
                            disabled={approveMut.isPending}
                            onClick={() => approveMut.mutate({ name: c.name, next: 'Rejected' })}
                          >
                            Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      No store customers for this filter
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
